import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  getFilePreviewUrl,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';

import {always} from 'ramda';
import {isNilOrEmpty, isString, isTrue, noop} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {
  ContextTarget,
  documentContextApi,
  useDeleteDocumentContextMutation,
  useGetEsignoServiceQuery,
  useLazyListEmailCredentialsQuery,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {downloadFile, handleApiError, printPDF, usePermissions} from '@dms/shared';
import {SendCustomerEmailForm} from '@dms/shared-aftersales';

import {Nullish, openFile} from 'shared/web';

import {ActionCallback} from 'features/eag-grid';

import {DOCUMENT_SIGNATURE_DIALOG_ID} from '../(Sections)/SignDocumentStepper/constants/DOCUMENT_SIGNATURE_DIALOG_ID';
import {SignDocumentStepper} from '../(Sections)/SignDocumentStepper/SignDocumentStepper';

export type GetDocumentDatagridActionsProps = {
  refreshData: () => void;
  contextTarget: ContextTarget;
  contextId: string;
  customerId?: string | Nullish;
  vehicleId?: string | Nullish;
  isDisabled?: boolean;
};

export const DOCUMENT_ACTION_COLUMNS = ['fileName', 'originalFileName', 'file'] as const;

export const useGetDocumentDatagridActions = (props: GetDocumentDatagridActionsProps) => {
  const {id} = useParams();
  const dispatch = useDispatch();

  const [getEmailCredentials] = useLazyListEmailCredentialsQuery();

  const {data: esigno} = useGetEsignoServiceQuery();

  const [canSignDocuments] = usePermissions({permissionKeys: ['signDocument']});

  const [deleteDocument] = useDeleteDocumentContextMutation();

  const isSignatureAvailable =
    isFeatureEnabled(featureFlags.DIGITAL_SIGNATURE) && canSignDocuments && isTrue(esigno?.enabled);

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === 'file_storage-file_uploaded') {
      props.refreshData();
    }
  });

  const documentActionCallback: ActionCallback<typeof DOCUMENT_ACTION_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const file = getColumnData<{remoteUrl: string; id: string; title: string; contentType: string}>(
      'fileName'
    );

    const contentType = file?.value?.contentType;
    const url = file?.value?.remoteUrl;
    const fileId = file?.value?.id;

    if (!(isString(url) && isString(rowId))) {
      showNotification.error();
      return;
    }

    const fileName = file?.value?.title ?? 'file';

    const previewUrl = getFilePreviewUrl(url, contentType);

    match(actionKey)
      .with('preview', 'openOnNewTab', () => openFile(previewUrl))
      .with('download', () => downloadFile(url, {fileType: 'attachment'}))
      .with('downloadOriginal', () => {
        const originalFile = getColumnData<{remoteUrl: string; id: string}>('originalFileName');
        const originalFileUrl = originalFile?.value?.remoteUrl;

        if (!originalFileUrl) {
          showNotification.error(i18n.t('entity.inspection.errors.invalidFileType'));
          return;
        }
        downloadFile(originalFileUrl, {fileType: 'attachment'});
      })
      .with('print', () => printPDF(previewUrl))
      .with('sign', () => {
        const originalFile = getColumnData<{remoteUrl: string; id: string}>('originalFileName', {
          isOptional: true,
        });
        const originalFileId = originalFile?.value?.id;
        const signableFileId = originalFileId ?? fileId;

        if (!signableFileId) {
          showNotification.error(i18n.t('general.labels.noPermission'));
          return;
        }

        if (!isFeatureEnabled(featureFlags.DIGITAL_SIGNATURE)) {
          showNotification.error(i18n.t('general.label.featureNotEnabled'));
          return;
        }

        openDialog(
          <SignDocumentStepper
            fileId={signableFileId}
            contextId={props.contextId}
            customerId={props.customerId}
            contextTarget={props.contextTarget}
            refreshData={props.refreshData}
            data-testid="documentSignature"
          />,
          {
            title: i18n.t('entity.document.actions.signDocument'),
            withAdditionalFooter: true,
            id: DOCUMENT_SIGNATURE_DIALOG_ID,
            size: 'large',
          }
        );
      })
      .with('send_email', () => {
        if (!fileId) {
          showNotification.error();
          return;
        }

        getEmailCredentials({})
          .unwrap()
          .then((emailCredentials) => {
            if (isNilOrEmpty(emailCredentials.credentials)) {
              showNotification.error(i18n.t('general.notifications.setupEmailProvider'));
              return;
            }

            // RTKq unwrap bullshit error
            // eslint-disable-next-line no-restricted-syntax
            const resourceId = match(props.contextTarget)
              .with('business-case', always(EntityResourceIds.businessCase))
              .with('service-case', always(EntityResourceIds.serviceCase))
              .with('vehicle', always(EntityResourceIds.vehicle))
              .with('rental-order', always(EntityResourceIds.rentalOrder))
              .with('customer', always(EntityResourceIds.customer))
              .with('service-case-order', always(EntityResourceIds.serviceOrder))
              .with('service-order-return', always(EntityResourceIds.serviceOrderReturn))
              .with('service-order-issue-note', always(EntityResourceIds.serviceOrderIssues))
              .with('direct-sale', always(EntityResourceIds.directSales))
              .with('tire-order', always(EntityResourceIds.tireOrder))
              .otherwise(always(EntityResourceIds.customer));

            openDialog(
              <SendCustomerEmailForm
                customerId={props.customerId}
                recordId={props.contextId}
                resourceId={resourceId}
                vehicleId={props.vehicleId}
                defaultValues={{
                  attachments: [
                    {
                      fileId,
                      fileUrl: url,
                      fileName,
                      fileType: contentType ?? 'unknown',
                      shouldConvertToPdf: false,
                    },
                  ],
                }}
              />,
              {
                scrollBehavior: 'outside',
                size: 'large',
                title: i18n.t('general.actions.sendEmail'),
              }
            );
          })
          .catch(handleApiError);
      })
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () => {
            deleteDocument({documentContextId: rowId})
              .unwrap()
              .then(() => refreshData())
              .then(() => {
                dispatch(
                  documentContextApi.util.invalidateTags([{type: 'documentsCount', id: id ?? ''}])
                );
              })
              .catch(handleApiError);
          },
        })
      )
      .otherwise(noop);
  };

  const documentSignatureAction: Action[] = isSignatureAvailable
    ? [
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'editor/border_color',
          title: i18n.t('entity.document.actions.signMultipleDocument'),
          isDisabled: props.isDisabled,
          onClick: () =>
            openDialog(
              <SignDocumentStepper
                contextId={props.contextId}
                customerId={props.customerId}
                contextTarget={props.contextTarget}
                refreshData={props.refreshData}
                data-testid="documentSignature"
              />,
              {
                title: i18n.t('entity.document.actions.signDocument'),
                withAdditionalFooter: true,
                id: DOCUMENT_SIGNATURE_DIALOG_ID,
                size: 'large',
              }
            ),
        },
      ]
    : [];

  return [documentActionCallback, documentSignatureAction] as const;
};
